import { useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { Check, X } from 'lucide-react';
import { Button, Card, FormField, Input, Modal } from '@/ui';
import { postFirstLogin } from '@/api/iam';
import { refineLocation, type TrLocation } from '@/lib/locations';
import { LocationFields } from '@/features/locations/LocationFields';
import { useLocations } from '@/features/locations/useLocations';
import { useSessionStore } from '@/auth/session.store';
import { zoneHome } from '@/auth/zone-home';
import { LegalReader } from '@/features/legal/LegalReader';
import {
  KVKK_TEXT,
  LEGAL_COPY,
  LEGAL_DOC_VERSION,
  USAGE_AGREEMENT_TEXT,
} from '@/features/legal/legal-copy';
import { PASSWORD_MIN_LENGTH, PASSWORD_RULES } from './password-policy';
import { authErrorMessage } from './error-message';
import styles from './AuthScreens.module.css';

/**
 * ZORUNLU ILK GIRIS.
 *
 * Hesabi buro/platform yoneticisi aciyor, gecici sifre e-postayla gidiyor ve
 * `mustChangePassword` isaretli geliyor. Bu ekrandan KACIS YOK (bkz.
 * RequireFreshPassword) — gecici sifreyle dolasan bir oturum, e-postasi
 * sizmis herkesin oturumu demek.
 *
 * Alan kumesi ve dogrulama anahtarlari mobil taraftaki FirstLoginSetup ile
 * BIREBIR ayni (dovizhub-mobile/src/features/iam/screens/FirstLoginSetup).
 * Sunucu ayni cagrida sehir/ilce/telefon/TC kimlik ZORUNLU tutuyor
 * (PROFILE_FIELDS_REQUIRED) — eksik gonderen bir form 400 alir.
 *
 * Sunucu bu cagrida TUM oturumlari dusurup yeni bir jeton cifti basiyor; donen
 * jetonlar yerine yazilmazsa kullanici kendi kaydettigi sifreyle aninda disari
 * atilir.
 */

const makeFirstLoginSchema = (t: TFunction, locations: readonly TrLocation[] = []) =>
  z
    .object({
      userCity: z.string().trim().min(1, { message: t('validation.cityRequired') }),
      userDistrict: z.string().trim().min(1, { message: t('validation.districtRequired') }),
      userPhone: z.string().trim().min(1, { message: t('validation.phoneRequired') }),
      userIdentityId: z.string().trim().min(1, { message: t('validation.identityRequired') }),
      userPassword: z
        .string()
        .min(1, { message: t('validation.passwordRequired') })
        .superRefine((value, ctx) => {
          // Sunucuyla AYNI SIRA: ilk karsilanmayan kural raporlanir, boylece
          // kullanici formda ve sunucuda ayni mesaji gorur.
          const failed = PASSWORD_RULES.find((rule) => !rule.test(value));
          if (!failed) return;
          ctx.addIssue({
            code: 'custom',
            message: t(failed.messageKey, { min: PASSWORD_MIN_LENGTH }),
          });
        }),
      confirmPassword: z.string().min(1, { message: t('auth.passwordRepeatRequired') }),
      // `z.literal(true)` DEGIL: giris tipi `true` olurdu ve formun baslangic
      // degeri (isaretsiz kutu) tipe uymazdi. Kural ayni, tip dogru.
      kvkkAcknowledged: z
        .boolean()
        .refine((value) => value, { message: LEGAL_COPY.signup.kvkkRequired }),
      usageAgreementAccepted: z
        .boolean()
        .refine((value) => value, { message: LEGAL_COPY.signup.usageRequired }),
    })
    .refine((values) => values.userPassword === values.confirmPassword, {
      message: t('auth.passwordsMismatch'),
      path: ['confirmPassword'],
    })
    // Il/ilce listeden (M4): serbest metin donemi kalintisi bir deger kaydedilemez.
    .superRefine(
      refineLocation({
        locations,
        cityField: 'userCity',
        districtField: 'userDistrict',
        message: t('locations.pickFromList'),
      }),
    );

type FirstLoginFormValues = z.input<ReturnType<typeof makeFirstLoginSchema>>;

type LegalDocKind = 'kvkk' | 'usage';

export function FirstLoginSetup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const snapshot = useSessionStore((state) => state.snapshot);
  const adoptAuthPayload = useSessionStore((state) => state.adoptAuthPayload);

  const [serverError, setServerError] = useState<string | null>(null);
  const [openDoc, setOpenDoc] = useState<LegalDocKind | null>(null);
  const [docReadToEnd, setDocReadToEnd] = useState(false);
  const [readDocs, setReadDocs] = useState<Record<LegalDocKind, boolean>>({
    kvkk: false,
    usage: false,
  });

  const locations = useLocations();
  const schema = useMemo(() => makeFirstLoginSchema(t, locations), [t, locations]);
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FirstLoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      userCity: '',
      userDistrict: '',
      userPhone: '',
      userIdentityId: '',
      userPassword: '',
      confirmPassword: '',
      kvkkAcknowledged: false,
      usageAgreementAccepted: false,
    },
  });

  // `watch()` DEGIL `useWatch`: watch bir FONKSIYON dondurur, React Compiler onu
  // guvenle hafizaya alamaz ve bayat arayuz uretebilir (eslint kurali:
  // react-hooks/incompatible-library). useWatch degerin kendisini abone eder.
  const password = useWatch({ control, name: 'userPassword' }) ?? '';
  const kvkkChecked = useWatch({ control, name: 'kvkkAcknowledged' }) === true;
  const usageChecked = useWatch({ control, name: 'usageAgreementAccepted' }) === true;

  // Sifresi zaten guncel biri bu rotaya gelirse burada tutulmaz.
  if (snapshot && !snapshot.mustChangePassword) {
    return <Navigate to={zoneHome(snapshot)} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      const payload = await postFirstLogin({
        userPassword: values.userPassword,
        userPhone: values.userPhone.trim(),
        userIdentityId: values.userIdentityId.trim(),
        userCity: values.userCity.trim(),
        userDistrict: values.userDistrict.trim(),
        kvkkAcknowledged: true,
        usageAgreementAccepted: true,
        // Hangi SURUMUN onaylandigi kayda giriyor; metin degisirse surum artar.
        legalDocVersion: LEGAL_DOC_VERSION,
      });
      if (payload.code !== 'success') {
        setServerError(payload.message ?? t('auth.genericProcessError'));
        return;
      }
      const session = adoptAuthPayload(payload);
      if (!session) {
        setServerError(t('auth.genericProcessError'));
        return;
      }
      void navigate(zoneHome(session), { replace: true });
    } catch (error) {
      setServerError(authErrorMessage(error, t));
    }
  });

  const docCopy = openDoc === 'usage' ? LEGAL_COPY.usage : LEGAL_COPY.kvkk;
  const docText = openDoc === 'usage' ? USAGE_AGREEMENT_TEXT : KVKK_TEXT;

  const openDocument = (kind: LegalDocKind): void => {
    setDocReadToEnd(false);
    setOpenDoc(kind);
  };

  const confirmDocument = (): void => {
    if (!openDoc) return;
    setReadDocs((current) => ({ ...current, [openDoc]: true }));
    setValue(openDoc === 'kvkk' ? 'kvkkAcknowledged' : 'usageAgreementAccepted', true, {
      shouldValidate: true,
    });
    setOpenDoc(null);
  };

  return (
    <Card padding="lg" className={styles.card}>
      <h1 className={styles.title}>{t('profile.firstLoginTitle')}</h1>
      <p className={styles.subtitle}>{t('profile.firstLoginSubtitle')}</p>

      <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
        <LocationFields
          control={control}
          register={register}
          setValue={setValue}
          cityName="userCity"
          districtName="userDistrict"
          cityLabel={t('fields.userCity')}
          districtLabel={t('fields.userDistrict')}
          required
        />

        <FormField label={t('fields.userPhone')} error={errors.userPhone} required>
          <Input type="tel" inputMode="tel" autoComplete="tel" {...register('userPhone')} />
        </FormField>

        <FormField label={t('fields.userIdentityId')} error={errors.userIdentityId} required>
          <Input inputMode="numeric" autoComplete="off" {...register('userIdentityId')} />
        </FormField>

        <FormField label={t('fields.userPassword')} error={errors.userPassword} required>
          <Input type="password" autoComplete="new-password" {...register('userPassword')} />
        </FormField>

        {/* Kural durumu CANLI: kullanici hangi kurali karsilamadigini yazarken
            gorur, gonderdikten sonra degil. */}
        <ul className={styles.policy} aria-label={t('auth.passwordPolicy')}>
          {PASSWORD_RULES.map((rule) => {
            const met = rule.test(password);
            return (
              <li key={rule.id} className={met ? styles.policyOk : styles.policyPending}>
                {met ? (
                  <Check size={14} aria-hidden="true" />
                ) : (
                  <X size={14} aria-hidden="true" />
                )}
                <span>{t(rule.messageKey, { min: PASSWORD_MIN_LENGTH })}</span>
              </li>
            );
          })}
        </ul>

        <FormField
          label={t('fields.userPasswordRepeat')}
          error={errors.confirmPassword}
          required
        >
          <Input type="password" autoComplete="new-password" {...register('confirmPassword')} />
        </FormField>

        <ConsentCheckbox
          checked={kvkkChecked}
          unlocked={readDocs.kvkk}
          label={LEGAL_COPY.signup.kvkkLabel}
          lockedHint={LEGAL_COPY.signup.mustReadFirst}
          readLabel={LEGAL_COPY.signup.readLink}
          error={errors.kvkkAcknowledged?.message}
          onRead={() => {
            openDocument('kvkk');
          }}
          onChange={(next) => {
            setValue('kvkkAcknowledged', next, { shouldValidate: true });
          }}
        />

        <ConsentCheckbox
          checked={usageChecked}
          unlocked={readDocs.usage}
          label={LEGAL_COPY.signup.usageLabel}
          lockedHint={LEGAL_COPY.signup.mustReadFirst}
          readLabel={LEGAL_COPY.signup.readLink}
          error={errors.usageAgreementAccepted?.message}
          onRead={() => {
            openDocument('usage');
          }}
          onChange={(next) => {
            setValue('usageAgreementAccepted', next, { shouldValidate: true });
          }}
        />

        {serverError ? (
          <p className={styles.error} role="alert">
            {serverError}
          </p>
        ) : null}

        <Button type="submit" size="lg" block loading={isSubmitting}>
          {t('profile.complete')}
        </Button>
      </form>

      <Modal
        open={openDoc !== null}
        onClose={() => {
          setOpenDoc(null);
        }}
        title={docCopy.title}
        width="lg"
        dismissOnScrim={false}
        footer={
          <div className={styles.modalFooter}>
            <span className={docReadToEnd ? styles.policyOk : styles.policyPending}>
              {docReadToEnd ? docCopy.scrolledEndLabel : LEGAL_COPY.signup.scrollToEnd}
            </span>
            <Button
              size="sm"
              disabled={!docReadToEnd}
              onClick={confirmDocument}
            >
              {LEGAL_COPY.signup.confirmRead}
            </Button>
          </div>
        }
      >
        <LegalReader
          text={docText}
          onReachEnd={() => {
            setDocReadToEnd(true);
          }}
        />
      </Modal>
    </Card>
  );
}

type ConsentCheckboxProps = {
  checked: boolean;
  /** Metin acilip sonuna kadar okundu mu — okunmadan kutu kilitli. */
  unlocked: boolean;
  label: string;
  lockedHint: string;
  readLabel: string;
  error?: string | undefined;
  onRead: () => void;
  onChange: (next: boolean) => void;
};

/**
 * Onay kutusu, METIN OKUNMADAN acilmaz.
 *
 * Kaydedilen sey `kvkkAcknowledged` + `legalDocVersion`, yani "bu surumu
 * okudum" iddiasi. Metni hic acmadan isaretlenebilen bir kutu, o kaydi
 * dogru olmayan bir seye donusturur.
 */
function ConsentCheckbox({
  checked,
  unlocked,
  label,
  lockedHint,
  readLabel,
  error,
  onRead,
  onChange,
}: ConsentCheckboxProps) {
  return (
    <div className={styles.consent}>
      <label className={styles.consentRow}>
        <input
          type="checkbox"
          checked={checked}
          disabled={!unlocked}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
        />
        <span>{label}</span>
      </label>
      <div className={styles.consentActions}>
        <Button tone="ghost" size="sm" onClick={onRead}>
          {readLabel}
        </Button>
        {!unlocked ? <span className={styles.consentHint}>{lockedHint}</span> : null}
      </div>
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
